// ===================================
// UTILS - Plantilla HTML del ticket térmico
// ===================================
// Extraído de usePrintBoleta para poder reutilizarlo tanto en el flujo interno
// (impresión autenticada de una venta) como en la consulta pública de boletas/vouchers,
// que obtiene los mismos datos desde un endpoint distinto.

import { getScaledFontScale, printPx, type PrintConfig } from '../hooks/usePrintConfig';

export interface ReciboDetalleItem {
  nombreProducto: string;
  cantidad: number;
  precioUnitario: number;
  /** Monto de la línea neto de descuento (el cobrado y el que va al DTE). */
  total: number;
  descuento?: number;
  /** Monto de la línea a precio de lista, antes del descuento. */
  subtotalSinDescuento?: number;
}

// Anchos de las columnas Cant. / Producto / Total del detalle (suman 100). Cant. necesita
// holgura para que el encabezado no se pegue a "Producto" en papel de 58 mm.
const COLUMN_WIDTH_PCT = { cantidad: 18, producto: 50, total: 32 } as const;

export const formatClp = (monto: number): string => `$${monto.toLocaleString('es-CL')}`;

// Forma mínima compartida por BoletaDetallePrint (@almacenes/core) y por la respuesta
// pública de consulta de boleta: cualquier objeto con estos campos puede imprimirse.
export interface ReciboParaTicket {
  boletaId: number;
  electronicReceipt: boolean;
  siiDteEmitidoId: number | null;
  siiFolio: number | null;
  contribuyenteRazonSocial: string | null;
  contribuyenteGiro: string | null;
  rut: string;
  nombreAlmacen: string;
  direccionAlmacen: string;
  numeroLocal: string | null;
  logoUrl: string | null;
  fecha: string;
  details: ReciboDetalleItem[];
  iva: number;
  descuento: number;
  /** Suma de los montos de línea a precio de lista, antes de descuentos. */
  subtotalSinDescuento?: number;
  total: number;
}

/**
 * Un voucher con descuento muestra cada línea a precio de lista y deja el descuento como renglón
 * aparte (Subtotal - Descuento = Total). Si el servidor no entrega los montos a precio de lista,
 * se muestra como antes. La boleta electrónica conserva el monto neto de línea del DTE.
 */
export const muestraPrecioLista = (
  boleta: Pick<ReciboParaTicket, 'electronicReceipt' | 'descuento' | 'subtotalSinDescuento'>
): boolean => !boleta.electronicReceipt && boleta.descuento > 0 && boleta.subtotalSinDescuento != null;

/** Monto a mostrar en la línea: a precio de lista cuando corresponde, neto en los demás casos. */
export const montoDeLinea = (
  boleta: Pick<ReciboParaTicket, 'electronicReceipt' | 'descuento' | 'subtotalSinDescuento'>,
  item: Pick<ReciboDetalleItem, 'total' | 'subtotalSinDescuento'>
): number => (muestraPrecioLista(boleta) ? (item.subtotalSinDescuento ?? item.total) : item.total);

export const formatRut = (rut: string): string => {
  const [numero, dv] = rut.split('-');
  const numLimpio = numero.replace(/\./g, '');
  return `${numLimpio.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}-${dv}`;
};

export const formatFecha = (fecha: string): string => {
  const d = new Date(fecha);
  const dd = d.getDate().toString().padStart(2, '0');
  const mm = (d.getMonth() + 1).toString().padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

export const formatHora = (fecha: string): string => {
  const d = new Date(fecha);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
};

/**
 * Obtiene la imagen del timbre PDF417 para una boleta electrónica.
 * Nunca lanza: si el servicio falla o no aplica (voucher), retorna cadena vacía
 * y la impresión continúa sin timbre.
 */
export const fetchPdf417 = async (boleta: ReciboParaTicket): Promise<string> => {
  if (!boleta.electronicReceipt || !boleta.siiDteEmitidoId) {
    return '';
  }

  try {
    const rutNumero = parseInt(boleta.rut.split('-')[0].replace(/\./g, ''));

    const requestBody = {
      rutAlmacen: rutNumero,
      siiDteEmitidoID: boleta.siiDteEmitidoId,
      total: boleta.total,
      detalles: boleta.details.map(item => ({
        nombre: item.nombreProducto,
        cantidad: item.cantidad,
        precio: item.precioUnitario,
        total: item.total,
      })),
    };

    // Usar URL relativa para aprovechar el proxy de Vite en desarrollo
    // En producción, cambiar a la URL real del servidor
    const url = import.meta.env.DEV
      ? '/sii_war_exploded/boleta/generarContenidoPDF417'
      : 'https://almacenesdigitales.cl/sii-pdf417/boleta/generarContenidoPDF417';

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (response.ok) {
      const responseText = await response.text();
      const data = JSON.parse(responseText);
      if (data.status === 200 && data.imagenBase64) {
        return data.imagenBase64;
      }
    }
  } catch (error) {
    console.error('❌ ERROR AL OBTENER PDF417:', error);
  }

  return '';
};

/**
 * Construye el HTML del ticket térmico (boleta electrónica o voucher) a partir del
 * detalle de la venta, el timbre PDF417 (si aplica) y la configuración de impresión
 * del usuario (ancho de papel, tamaño de fuente, escala de calibración de impresora).
 */
export const buildTicketHtml = (
  boleta: ReciboParaTicket,
  imagenPDF417: string,
  printConfig: PrintConfig
): string => {
  const s = getScaledFontScale(printConfig);
  const px = (n: number) => printPx(printConfig, n);

  // font-family se aplica en body desde PrintPage — no se repite en cada celda
  // Descuento de la línea, como renglón aparte bajo el producto
  const filaDescuentoLinea = (item: ReciboDetalleItem) =>
    item.descuento && item.descuento > 0
      ? `
    <tr>
      <td></td>
      <td style="padding:${px(1)}px;font-size:${s.xs}px;font-style:italic">Descuento</td>
      <td style="text-align:right;padding:${px(1)}px;white-space:nowrap;font-size:${s.xs}px;font-style:italic">-${formatClp(item.descuento)}</td>
    </tr>`
      : '';

  const filaDetalle = (item: ReciboDetalleItem) => `
    <tr>
      <td style="text-align:center;padding:${px(1)}px;width:${COLUMN_WIDTH_PCT.cantidad}%;font-size:${s.sm}px">${item.cantidad % 1 === 0 ? item.cantidad.toFixed(0) : item.cantidad}</td>
      <td style="padding:${px(1)}px;width:${COLUMN_WIDTH_PCT.producto}%;word-wrap:break-word;font-size:${s.sm}px">${item.nombreProducto}</td>
      <td style="text-align:right;padding:${px(1)}px;width:${COLUMN_WIDTH_PCT.total}%;white-space:nowrap;font-size:${s.sm}px">${formatClp(montoDeLinea(boleta, item))}</td>
    </tr>${filaDescuentoLinea(item)}`;

  const detalleHTML = boleta.details.map(filaDetalle).join('');

  const encabezadoDetalleHTML = `
        <tr style="border-bottom:1px dashed #000">
          <th style="text-align:center;width:${COLUMN_WIDTH_PCT.cantidad}%;white-space:nowrap;font-size:${s.sm}px">Cant.</th>
          <th style="text-align:left;width:${COLUMN_WIDTH_PCT.producto}%;font-size:${s.sm}px">Producto</th>
          <th style="text-align:right;width:${COLUMN_WIDTH_PCT.total}%;white-space:nowrap;font-size:${s.sm}px">Total</th>
        </tr>`;

  const subtotalHTML = muestraPrecioLista(boleta)
    ? `<tr><td colspan="2" style="padding:${px(1)}px;text-align:left;font-size:${s.sm}px">Subtotal:</td><td style="text-align:right;padding:${px(1)}px;white-space:nowrap;font-size:${s.sm}px">${formatClp(boleta.subtotalSinDescuento ?? boleta.total)}</td></tr>`
    : '';

  const descuentoHTML = boleta.descuento > 0
    ? `<tr><td colspan="2" style="padding:${px(1)}px;text-align:left;font-size:${s.sm}px">Dscto Total:</td><td style="text-align:right;padding:${px(1)}px;white-space:nowrap;font-size:${s.sm}px">-${formatClp(boleta.descuento)}</td></tr>`
    : '';

  const logoHTML = boleta.logoUrl
    ? `<div style="text-align:center;margin-bottom:${px(6)}px"><img src="${boleta.logoUrl}" alt="Logo" style="max-width:${px(60)}px;max-height:${px(60)}px;object-fit:contain;display:block;margin:0 auto;"/></div>`
    : '';

  if (boleta.electronicReceipt) {
    const montoNeto = boleta.iva > 0 ? boleta.total - boleta.iva : 0;
    const netoHTML = boleta.iva > 0
      ? `<tr><td colspan="2" style="padding:${px(1)}px;text-align:left;font-size:${s.sm}px">Monto Neto:</td><td style="text-align:right;padding:${px(1)}px;white-space:nowrap;font-size:${s.sm}px">$${montoNeto.toLocaleString('es-CL')}</td></tr>`
      : '';
    const ivaHTML = boleta.iva > 0
      ? `<tr><td colspan="2" style="padding:${px(1)}px;text-align:left;font-size:${s.sm}px">IVA (19%):</td><td style="text-align:right;padding:${px(1)}px;white-space:nowrap;font-size:${s.sm}px">$${boleta.iva.toLocaleString('es-CL')}</td></tr>`
      : '';

    return `
      ${logoHTML}
      <div style="text-align:center;margin-bottom:${px(6)}px;font-size:${s.base}px">
        <div style="font-weight:bold">${boleta.contribuyenteRazonSocial}</div>
        <div>${boleta.contribuyenteGiro}</div>
        <div style="font-weight:bold">${boleta.nombreAlmacen}</div>
        <div>RUT: ${formatRut(boleta.rut)}</div>
        <div>${boleta.direccionAlmacen}</div>
        ${boleta.numeroLocal ? `<div>${boleta.numeroLocal}</div>` : ''}
        <div style="font-weight:bold;margin-top:${px(4)}px">BOLETA ELECTRÓNICA N° ${boleta.siiFolio}</div>
        <div style="font-size:${s.sm}px">${formatFecha(boleta.fecha)} ${formatHora(boleta.fecha)}</div>
      </div>
      <hr style="border:none;border-top:1px dashed #000;margin:${px(4)}px 0"/>
      <table style="width:100%;font-size:${s.sm}px;border-collapse:collapse;table-layout:fixed">
        <thead>${encabezadoDetalleHTML}</thead>
        <tbody>${detalleHTML}</tbody>
      </table>
      <hr style="border:none;border-top:1px dashed #000;margin:${px(4)}px 0"/>
      <table style="width:100%;font-size:${s.sm}px;table-layout:fixed">
        ${netoHTML}
        ${ivaHTML}
        ${descuentoHTML}
        <tr style="font-weight:bold;font-size:${s.md}px">
          <td colspan="2" style="padding:${px(1)}px;text-align:left">TOTAL:</td>
          <td style="text-align:right;padding:${px(1)}px;white-space:nowrap">$${boleta.total.toLocaleString('es-CL')}</td>
        </tr>
      </table>
      <hr style="border:none;border-top:1px dashed #000;margin:${px(4)}px 0"/>
      ${imagenPDF417 ? `
      <div style="text-align:center;margin-top:${px(4)}px;margin-bottom:${px(4)}px">
        <img src="data:image/png;base64,${imagenPDF417}" alt="PDF417" style="max-width:100%;height:auto;display:block;margin:0 auto;"/>
      </div>` : ''}
      <div style="text-align:center;margin-top:${px(4)}px;font-size:${s.sm}px">
        <div>Verifique documento en www.sii.cl</div>
      </div>
      <hr style="border:none;border-top:1px dashed #000;margin:${px(4)}px 0"/>
      <div style="text-align:center;margin-top:${px(6)}px;font-size:${s.sm}px">
        <div>Almacenes Digitales</div>
        <div style="font-weight:bold">¡Gracias por tu compra!</div>
      </div>`;
  }

  // Voucher
  return `
    ${logoHTML}
    <div style="text-align:center;margin-bottom:${px(6)}px;font-size:${s.base}px">
      <div style="font-weight:bold;font-size:${s.title}px">VOUCHER N° ${boleta.boletaId}</div>
      <div>${formatRut(boleta.rut)}</div>
      <div style="font-weight:bold">${boleta.nombreAlmacen}</div>
      <div>${boleta.direccionAlmacen}</div>
      ${boleta.numeroLocal ? `<div>${boleta.numeroLocal}</div>` : ''}
      <div style="font-size:${s.sm}px">${formatFecha(boleta.fecha)} ${formatHora(boleta.fecha)}</div>
    </div>
    <hr style="border:none;border-top:1px dashed #000;margin:${px(4)}px 0"/>
    <table style="width:100%;font-size:${s.sm}px;border-collapse:collapse;table-layout:fixed">
      <thead>${encabezadoDetalleHTML}</thead>
      <tbody>${detalleHTML}</tbody>
    </table>
    <hr style="border:none;border-top:1px dashed #000;margin:${px(4)}px 0"/>
    <table style="width:100%;font-size:${s.sm}px;table-layout:fixed">
      ${subtotalHTML}
      ${descuentoHTML}
      <tr style="font-weight:bold;font-size:${s.md}px">
        <td colspan="2" style="padding:${px(1)}px;text-align:left">TOTAL:</td>
        <td style="text-align:right;padding:${px(1)}px;white-space:nowrap">${formatClp(boleta.total)}</td>
      </tr>
    </table>
    <hr style="border:none;border-top:1px dashed #000;margin:${px(4)}px 0"/>
    <div style="text-align:center;margin-top:${px(4)}px;font-size:${s.xs}px;font-style:italic">
      <div>*Voucher no válido como boleta electrónica*</div>
    </div>
    <hr style="border:none;border-top:1px dashed #000;margin:${px(4)}px 0"/>
    <div style="text-align:center;margin-top:${px(6)}px;font-size:${s.sm}px">
      <div>Almacenes Digitales</div>
      <div style="font-weight:bold">¡Gracias por tu compra!</div>
    </div>`;
};
